import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import RoadmapPath from '../../assets/images/RoadmapPath.png';
import RoadmapPathComplete from '../../assets/images/RoadmapPathComplete.png';
import { useDispatch } from 'react-redux';
import * as appActions from '../../Actions/app';
import { LevelData, LevelStatus, UserLevelData } from '../../Types';
import { find } from 'lodash';
import '../../index.css';
import './Path.css';
import { useNavigate } from 'react-router-dom';
import { isCapacitorNative } from 'Utils/Constants';
import TouchBox from 'Components/Capacitor/TouchBox';
import LevelPreviewSheet, { LevelPreviewSheetState } from './LevelPreviewSheet';

interface LevelDataProps {
  left: string;
  top: string;
  levelName: string;
  levelNumber: number;
  levelIndex: number;
  levelStatus: string;
  labelSide: string;
  isAccessible: boolean;
  highestTierComplete: number;
}

interface LevelBlobProps {
  lastLevel: number;
  levelSelect: number;
  data: LevelDataProps;
  levelCompletedAt: string | undefined;
  onSingleSelect: (levelIndex: number) => void;
  forceSingleTap: boolean;
}

const smallMobileScreenBreakWidth = 900;
const smallMobileScreenBreakHeight = 700;
const smallPhoneScreenBreak = 480;
const sheetPeekHeight = 136;

const heightBreakpoints = {
  small: 700,
  'medium-small': 840,
  medium: 905,
};

const endOffsetValues = {
  small: 20,
  'medium-small': 30,
  medium: 60,
  large: 60,
};

const heightOffsetValues = {
  small: 260,
  'medium-small': 320,
  medium: 400,
  large: 480,
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const readSafeInset = (cssVarName: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const raw = window.getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isSmallPhoneViewport = () => {
  if (typeof window === 'undefined') return false;
  return Math.min(window.innerWidth, window.innerHeight) <= smallPhoneScreenBreak;
};

const LevelBlob = (props: LevelBlobProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // LevelStatus should have one of 3 values: "not-started", "in-progress", "complete"
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const { levelSelect, levelCompletedAt } = props;
  const p = props.data;
  const pos = { left: p.left, top: p.top };
  const complete = p.levelStatus === LevelStatus.complete;
  const inProgress = p.levelStatus === LevelStatus.active;
  let chosenStyles = {};
  if (levelCompletedAt) {
    chosenStyles = completeContainerStyles;
  } else if (inProgress) {
    chosenStyles = inProgressContainerStyles;
  }
  const color = complete ? colors.color.complete : colors.color.incomplete;

  const Label = () => {
    return (
      <Box className="blob-text-styles" sx={{ color: colors.color.incomplete }}>
        <Box sx={{ whiteSpace: 'nowrap' }}>{p.levelName}</Box>
      </Box>
    );
  };

  const handleSingleClick = (levelIndex: number) => {
    dispatch(appActions.setLevelSelect({ levelSelect: levelIndex }));
    props.onSingleSelect(levelIndex);
  };

  const handleDoubleClick = (levelIndex: number, levelNumber: number, tierComplete: number) => {
    dispatch(appActions.setLevelSelect({ levelSelect: levelIndex }));
    dispatch(appActions.fetchTutorialData());
    // Level 0 is tutorial-only (no sight reading stage), so always go to tutorial
    // For other levels, skip tutorial if user has completed at least one tier
    if (tierComplete >= 1 && levelNumber > 0) {
      navigate(`/lesson`);
    } else {
      navigate(`/tutorial`);
    }
  };

  const handleLevelSelect = (levelIndex: number, levelNumber: number, tierComplete: number) => {
    // On small phones and native touch, single tap should always select/open preview.
    if (props.forceSingleTap || isCapacitorNative()) {
      handleSingleClick(levelIndex);
      return;
    }

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      handleDoubleClick(levelIndex, levelNumber, tierComplete);
    } else {
      const newTimeout = setTimeout(() => {
        handleSingleClick(levelIndex);
        setClickTimeout(null);
      }, 200); // double click delay
      setClickTimeout(newTimeout);
    }
  };

  const maxLevelIndex = props.lastLevel - 1;
  const minLevelIndex = 0;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        if (levelSelect < maxLevelIndex) {
          dispatch(appActions.setLevelSelect({ levelSelect: levelSelect + 1 }));
        }
      } else if (event.key === 'ArrowLeft') {
        if (levelSelect > minLevelIndex) {
          dispatch(appActions.setLevelSelect({ levelSelect: levelSelect - 1 }));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [levelSelect, dispatch, maxLevelIndex, minLevelIndex]);

  return (
    <TouchBox
      data-level-index={p.levelIndex}
      sx={{ ...nameContainerStyles, ...pos }}
      onClick={() => handleLevelSelect(p.levelIndex, p.levelNumber, p.highestTierComplete)}
    >
      {p.labelSide == 'top' && <Label />}
      <Box
        className="no-select"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          className="level-blob-circle"
          sx={{ ...circleContainerStyles, ...chosenStyles, position: 'relative' }}
        >
          <Box sx={{ ...numberStyles, ...{ color: color } }}>{p.levelNumber}</Box>
          {p.levelIndex == levelSelect && <Box sx={selectedStyles} />}
        </Box>
      </Box>
      {p.labelSide == 'bottom' && <Label />}
    </TouchBox>
  );
};

interface PathProps {
  lastLevelComplete: number;
  userLevelStatuses: Record<string, string>;
  scrollPos: number;
  setScrollPos: React.Dispatch<React.SetStateAction<number>>;
  setScrollAreaSize: React.Dispatch<React.SetStateAction<number>>;
  levelSelect: number;
  unitSelect: number;
  levelData: LevelData[];
  userLevelTiersComplete: Record<number, number>;
  areaToPreventScroll: string;
  setAreaToPreventScroll: React.Dispatch<React.SetStateAction<string>>;
  pathScrollPos: number;
  userLevelData: UserLevelData[];
  latestLevelRef: React.RefObject<HTMLDivElement | null>;
  isSmallMobile: boolean;
}

export const Path = (props: PathProps) => {
  const {
    levelSelect,
    levelData,
    unitSelect,
    lastLevelComplete,
    userLevelStatuses,
    userLevelTiersComplete,
    areaToPreventScroll,
    setScrollPos,
    setScrollAreaSize,
    setAreaToPreventScroll,
    pathScrollPos,
    userLevelData,
    latestLevelRef,
    isSmallMobile,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get the last complete level:
  const latestLevelRefContainer = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);

  const [sheetState, setSheetState] = useState<LevelPreviewSheetState>('closed');
  const [mapTranslateY, setMapTranslateY] = useState(0);
  const mapTranslateYRef = useRef(0);

  const checkScale = () => {
    if (window.innerHeight <= heightBreakpoints.small) {
      return 'small';
    } else if (window.innerHeight <= heightBreakpoints['medium-small']) {
      return 'medium-small';
    } else if (window.innerHeight <= heightBreakpoints.medium) {
      return 'medium';
    }
    return 'large';
  };

  const [screenSizeScale, setScreenSizeScale] = useState(checkScale());
  const [isSmallMobileScreen, setIsSmallMobileScreen] = useState(
    window.innerWidth <= smallMobileScreenBreakWidth ||
      window.innerHeight <= smallMobileScreenBreakHeight,
  );

  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const [safeInsets, setSafeInsets] = useState({
    top: readSafeInset('--safe-area-top'),
    bottom: readSafeInset('--safe-area-bottom'),
  });

  useEffect(() => {
    mapTranslateYRef.current = mapTranslateY;
  }, [mapTranslateY]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallMobileScreen(
        window.innerWidth <= smallMobileScreenBreakWidth ||
          window.innerHeight <= smallMobileScreenBreakHeight,
      );
      setScreenSizeScale(checkScale());
      setViewportHeight(window.innerHeight);
      setSafeInsets({
        top: readSafeInset('--safe-area-top'),
        bottom: readSafeInset('--safe-area-bottom'),
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isSmallMobile) {
      setSheetState('closed');
      setMapTranslateY(0);
      mapTranslateYRef.current = 0;
    }
  }, [isSmallMobile]);

  const [blobPositions, _pathPositions, basePxArray, xBlobPosVals, _pathCompleteVals] =
    constGeneratePositions(screenSizeScale, isSmallMobileScreen);

  // for where the roadmap path stops (visual display of completed path)
  // Handle -1 (no levels complete) case - show no completed path
  const curLevelPosition =
    lastLevelComplete >= 0
      ? (xBlobPosVals[xBlobPosVals.length - 1] + 60) *
          Math.floor(lastLevelComplete / xBlobPosVals.length) +
        xBlobPosVals[lastLevelComplete % xBlobPosVals.length] +
        60
      : 0;

  // Scroll position should center on the last selected level (shown by the ring), not highest completed
  const selectedLevelPosition =
    levelSelect >= 0
      ? (xBlobPosVals[xBlobPosVals.length - 1] + 60) *
          Math.floor(levelSelect / xBlobPosVals.length) +
        xBlobPosVals[levelSelect % xBlobPosVals.length] +
        60
      : 0;

  useEffect(() => {
    if (latestLevelRef.current && areaToPreventScroll === 'path') {
      latestLevelRef.current.scrollLeft = pathScrollPos;
    }
  }, [pathScrollPos, areaToPreventScroll, latestLevelRef]);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const denom = Math.max(1, target.scrollWidth - target.clientWidth);

      // Only skip when the minimap is the driver (it sets areaToPreventScroll = 'path')
      if (areaToPreventScroll !== 'path') {
        setScrollPos(target.scrollLeft / denom);
      }

      setScrollAreaSize(target.clientWidth / Math.max(1, target.scrollWidth));
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [areaToPreventScroll, latestLevelRef, setScrollAreaSize, setScrollPos]);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;

    const timeoutId = window.setTimeout(() => {
      if (selectedLevelPosition) {
        const containerWidth = el.clientWidth;
        el.scrollTo({
          left: selectedLevelPosition - containerWidth / 2 + 35,
          behavior: 'smooth',
        });
      }
    }, 250);

    setScrollPos(el.scrollLeft / Math.max(1, el.scrollWidth - el.clientWidth));
    setScrollAreaSize(el.clientWidth / Math.max(1, el.scrollWidth));

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;
    const handleDragStart = (e: DragEvent) => e.preventDefault();
    el.addEventListener('dragstart', handleDragStart);
    return () => el.removeEventListener('dragstart', handleDragStart);
  }, [latestLevelRef]);

  useEffect(() => {
    const el = latestLevelRef.current;
    if (!el) return;
    const preventNavigation = (e: WheelEvent) => {
      if (e.deltaX < 0 && (e.target as HTMLDivElement).scrollLeft <= 0 && el.scrollLeft <= 0) {
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', preventNavigation, { passive: false });
    return () => el.removeEventListener('wheel', preventNavigation);
  }, [latestLevelRef]);

  //let lastLevel = parseInt(Object.keys(Levels)[Object.keys(Levels).length -1])
  const lastLevel = levelData.length;
  const numImagesShow = [...Array(Math.floor(lastLevel / 12) + 1)]; // 12 levels can fit on one road map image, but need to load next image piece if on level 12.
  // ^^ Need to convert that math value into an array range!
  // Determin where to cut off the combined roadmap images based on where the last level lies!
  let blobLevelCutoff;
  const numPaths = Math.floor(lastLevel / xBlobPosVals.length);
  let builtBlobPositions: Array<{ left: string; top: string; labelSide: string }>;

  const endOffset = endOffsetValues[screenSizeScale as keyof typeof endOffsetValues];
  if (numPaths == 0) {
    blobLevelCutoff = `${xBlobPosVals[xBlobPosVals.length - 1] + 50}px`;
    builtBlobPositions = blobPositions;
  } else {
    blobLevelCutoff = `${
      xBlobPosVals[xBlobPosVals.length - 1] * numPaths +
      xBlobPosVals[lastLevel % xBlobPosVals.length] +
      numPaths * 50 -
      endOffset
    }px`;
    builtBlobPositions = blobPositions;
    void [...Array(numPaths)].map((_k, v) => {
      let currentBasePxArray = basePxArray;
      if (v == numPaths - 1) {
        currentBasePxArray = {
          x: basePxArray.x.slice(0, lastLevel % xBlobPosVals.length),
          y: basePxArray.y.slice(0, lastLevel % xBlobPosVals.length),
        };
      }
      const newBlobPositions = convertToPos(
        currentBasePxArray,
        (v + 1) * xBlobPosVals[xBlobPosVals.length - 1] + (v + 1) * 60,
      );
      builtBlobPositions = builtBlobPositions.concat(newBlobPositions);
    });
  }

  const blobCenterYBounds = useMemo(() => {
    const centers = builtBlobPositions
      .map((position) => Number.parseFloat(position.top) + 25)
      .filter((value) => Number.isFinite(value));

    return {
      min: centers.length ? Math.min(...centers) : -60,
      max: centers.length ? Math.max(...centers) : 400,
    };
  }, [builtBlobPositions]);

  const selectedLevel = levelData[levelSelect] || levelData[0];
  const selectedLevelNumber = selectedLevel?.level_number ?? 0;
  const highestTierCompleteForSelected = userLevelTiersComplete[selectedLevelNumber] || 0;

  const expandedSheetHeight = useMemo(() => {
    const computed = Math.min(viewportHeight * 0.7, viewportHeight - safeInsets.top - 16);
    return Math.max(sheetPeekHeight + 36, computed);
  }, [viewportHeight, safeInsets.top]);

  const peekSheetHeightWithSafeBottom = sheetPeekHeight + safeInsets.bottom;

  const currentSheetHeight =
    sheetState === 'closed'
      ? 0
      : sheetState === 'expanded'
        ? expandedSheetHeight
        : peekSheetHeightWithSafeBottom;

  const clampTranslateYToBounds = useCallback(
    (candidateTranslateY: number) => {
      const scrollContainer = latestLevelRef.current;
      if (!scrollContainer) return candidateTranslateY;

      const frameHeight = scrollContainer.clientHeight;
      const edgePadding = 24;
      const minTranslateY = frameHeight - edgePadding - blobCenterYBounds.max;
      const maxTranslateY = edgePadding - blobCenterYBounds.min;

      if (minTranslateY > maxTranslateY) return 0;
      return clamp(candidateTranslateY, minTranslateY, maxTranslateY);
    },
    [blobCenterYBounds.max, blobCenterYBounds.min, latestLevelRef],
  );

  const autoPanToLevel = useCallback(
    (selectedLevelIndex: number, sheetHeightOverride?: number) => {
      if (!isSmallMobile || !isSmallPhoneViewport()) return;

      const scrollContainer = latestLevelRef.current;
      if (!scrollContainer) return;

      const selectedDot = scrollContainer.querySelector(
        `[data-level-index="${selectedLevelIndex}"] .level-blob-circle`,
      ) as HTMLElement | null;
      if (!selectedDot) return;

      const dotRect = selectedDot.getBoundingClientRect();
      const screenX = dotRect.left + dotRect.width / 2;
      const screenY = dotRect.top + dotRect.height / 2;

      const viewportWidth = window.innerWidth;
      const viewportHeightNow = window.innerHeight;
      const safeTop = readSafeInset('--safe-area-top');

      const paddingTop = safeTop + 16;
      const paddingSide = 16;
      const targetY = 0.38 * viewportHeightNow;

      const sheetHeight = sheetHeightOverride ?? currentSheetHeight;
      const sheetTop = viewportHeightNow - sheetHeight;
      const minVisibleY = sheetTop - 24;

      const desiredY = clamp(targetY, paddingTop, minVisibleY);
      if (screenY > minVisibleY || screenY < paddingTop) {
        const deltaY = desiredY - screenY;
        const nextTranslateY = clampTranslateYToBounds(mapTranslateYRef.current + deltaY);
        if (Math.abs(nextTranslateY - mapTranslateYRef.current) > 0.5) {
          mapTranslateYRef.current = nextTranslateY;
          setMapTranslateY(nextTranslateY);
        }
      }

      const desiredX = clamp(screenX, paddingSide, viewportWidth - paddingSide);
      if (Math.abs(desiredX - screenX) > 0.5) {
        const deltaScroll = screenX - desiredX;
        const maxScrollLeft = Math.max(
          0,
          scrollContainer.scrollWidth - scrollContainer.clientWidth,
        );
        const nextScrollLeft = clamp(scrollContainer.scrollLeft + deltaScroll, 0, maxScrollLeft);
        scrollContainer.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
      }
    },
    [clampTranslateYToBounds, currentSheetHeight, isSmallMobile, latestLevelRef],
  );

  useEffect(() => {
    if (!isSmallMobile || sheetState === 'closed') return;

    const timeoutId = window.setTimeout(() => {
      autoPanToLevel(levelSelect);
    }, 40);

    return () => window.clearTimeout(timeoutId);
  }, [autoPanToLevel, levelSelect, isSmallMobile, sheetState, currentSheetHeight]);

  // Functions that allow clicking and dragging the mouse
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (areaToPreventScroll != 'minimap') {
      setAreaToPreventScroll('minimap');
    }
    if (isDragging && latestLevelRef.current) {
      const dx = startX - e.clientX;
      latestLevelRef.current.scrollLeft += dx;
      setStartX(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const onPlaySelectedLevel = useCallback(() => {
    dispatch(appActions.fetchTutorialData());
    if (highestTierCompleteForSelected >= 1 && selectedLevelNumber > 0) {
      navigate('/lesson');
    } else {
      navigate('/tutorial');
    }
  }, [dispatch, highestTierCompleteForSelected, navigate, selectedLevelNumber]);

  const onLevelSingleSelect = useCallback(
    (selectedLevelIndex: number) => {
      if (!isSmallMobile) return;

      const nextSheetState = sheetState === 'closed' ? 'peek' : sheetState;
      if (nextSheetState !== sheetState) {
        setSheetState(nextSheetState);
      }

      const nextHeight =
        nextSheetState === 'expanded' ? expandedSheetHeight : peekSheetHeightWithSafeBottom;
      window.setTimeout(() => {
        autoPanToLevel(selectedLevelIndex, nextHeight);
      }, 40);
    },
    [autoPanToLevel, expandedSheetHeight, isSmallMobile, peekSheetHeightWithSafeBottom, sheetState],
  );

  const heightOffset = heightOffsetValues[screenSizeScale as keyof typeof endOffsetValues];

  return (
    <Box className="path-container-styles">
      <Box ref={latestLevelRefContainer}>
        <Box
          className="chatbox-scroll-container"
          style={{
            width: '100%',
            position: 'relative',
            paddingBottom: '50px',
            paddingTop: '50px',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
          ref={latestLevelRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <Box
            sx={{
              transform: `translateY(${mapTranslateY}px)`,
              transition: 'transform 200ms ease-out',
              willChange: 'transform',
            }}
          >
            <Box sx={{ position: 'absolute' }}>
              {builtBlobPositions.map(
                (e: { left: string; top: string; labelSide: string }, i: number) => {
                  const levelNum: number =
                    unitSelect === 2 && levelData.length >= i + 26 ? i + 26 : i;
                  const levelStatus =
                    find(userLevelStatuses, (_, key) => key == `${i}`) || 'inactive';
                  const highestTierComplete = userLevelTiersComplete[levelNum]
                    ? userLevelTiersComplete[levelNum]
                    : 0;
                  let isAccessible = false;
                  const levelName = find(
                    levelData,
                    (datum: LevelData) => datum.level_number === levelNum,
                  )?.name;
                  const levelCompletedAt = find(
                    userLevelData,
                    (datum: UserLevelData) => datum?.level?.level_number === levelNum,
                  )?.level_completed_at;
                  if (find(levelData, (datum: LevelData) => datum?.level_number === levelNum)) {
                    isAccessible = true;
                  } else {
                    return <Box key={`blob-${i}`}></Box>;
                  }
                  return (
                    <Box key={`blob-${i}`}>
                      <LevelBlob
                        lastLevel={lastLevel}
                        levelCompletedAt={levelCompletedAt}
                        levelSelect={levelSelect}
                        onSingleSelect={onLevelSingleSelect}
                        forceSingleTap={isSmallMobile}
                        data={{
                          ...e,
                          levelIndex: i,
                          levelNumber: levelNum,
                          levelName: levelName || '',
                          levelStatus: levelStatus,
                          highestTierComplete: highestTierComplete,
                          isAccessible: isAccessible,
                        }}
                      />
                    </Box>
                  );
                },
              )}
            </Box>

            <Box
              sx={{
                width: blobLevelCutoff,
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                marginRight: '100px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: blobLevelCutoff,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {numImagesShow.map((_k, v) => {
                  return (
                    <Box key={`img-${v}`}>
                      <img
                        className="roadmap-img-styles"
                        src={RoadmapPath}
                        style={{
                          height: isSmallMobileScreen ? 200 : heightOffset,
                        }}
                      />
                    </Box>
                  );
                })}

                <Box
                  sx={{
                    position: 'absolute',
                    width: `${curLevelPosition}px`,
                    overflow: 'hidden',
                    display: 'flex',
                  }}
                >
                  {numImagesShow.map((_k, v) => {
                    return (
                      <Box key={`img-complete-${v}`}>
                        <img
                          className="roadmap-img-styles"
                          src={RoadmapPathComplete}
                          style={{
                            height: isSmallMobileScreen ? 200 : heightOffset,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {unitSelect == 1 && (
                <Box>
                  <Box
                    sx={{
                      background:
                        'linear-gradient(90deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.68) 65.21%)',
                      position: 'absolute',
                      top: '-300px',
                      bottom: '-300px',
                      width: '400px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        color: '#CED0D4',
                        textAlign: 'center',
                        fontFamily: 'Lato',
                        fontSize: '24px',
                        fonttyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                        letterSpacing: '0.24px',
                      }}
                    >
                      Unit 2 Coming Soon!
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {isSmallMobile && (
        <LevelPreviewSheet
          state={sheetState}
          level={selectedLevel}
          peekHeight={sheetPeekHeight}
          expandedHeight={expandedSheetHeight}
          safeBottom={safeInsets.bottom}
          onStateChange={(nextState) => {
            setSheetState(nextState);
          }}
          onPlay={onPlaySelectedLevel}
        />
      )}
    </Box>
  );
};

// Things needed for the roadmap... make it continuous for all levels
// Roadmap is fixed png, so can generate the positions in a cyclical maner
// The path position should just be a function of that

const constGeneratePositions = (
  screenSizeScale: string,
  isSmallMobileScreen: boolean,
): [
  Array<{ left: string; top: string; labelSide: string }>,
  Array<{ width: string }>,
  BasePxArray,
  number[],
  number[],
] => {
  // We'll call this the medium.
  let basePxArray = {
    x: [92, 265, 422, 576, 750, 886, 1022, 1173, 1320, 1460, 1620, 1766], // This will continue as fixed values until the loop pattern repeasts
    y: [305, 10, 212, -60, 305, 55, 368, -37, 280, 15, 310, 110], // Same as above
  };

  if (screenSizeScale == 'small') {
    let heightReductionFactor = 0.65;
    let horizontalOffset = 23;
    let verticalOffset = 17;
    if (isSmallMobileScreen) {
      heightReductionFactor = 0.502;
      horizontalOffset = 33;
      verticalOffset = 19;
    }
    basePxArray = {
      x: basePxArray.x.map((value) => value * heightReductionFactor - horizontalOffset),
      y: basePxArray.y.map((value) => value * heightReductionFactor - verticalOffset),
    };
  } else if (screenSizeScale == 'medium-small') {
    const heightReductionFactor = 0.805;
    const horizontalOffset = 20;
    const verticalOffset = 10;
    basePxArray = {
      x: basePxArray.x.map((value) => value * heightReductionFactor - horizontalOffset),
      y: basePxArray.y.map((value) => value * heightReductionFactor - verticalOffset),
    };
  } else if (screenSizeScale == 'large') {
    const heightReductionFactor = 1.205;
    const horizontalOffset = -3;
    const verticalOffset = -8;
    basePxArray = {
      x: basePxArray.x.map((value) => value * heightReductionFactor - horizontalOffset),
      y: basePxArray.y.map((value) => value * heightReductionFactor - verticalOffset),
    };
  }

  const pathOffsetVAlue = 35;
  const pathCompleteVals = basePxArray.x.map((v) => {
    return v + pathOffsetVAlue;
  });

  const blobPositions = convertToPos(basePxArray);
  const pathPositions = pathCompleteVals.map((v) => ({
    width: `${v}px`,
  }));
  const xBlobPosVals = basePxArray.x;
  return [blobPositions, pathPositions, basePxArray, xBlobPosVals, pathCompleteVals];
};

interface BasePxArray {
  x: number[];
  y: number[];
}

const convertToPos = (basePxArray: BasePxArray, offset = 0) => {
  const blobPositions = basePxArray.x.map((_e: number, i: number) => {
    const labelSide = i % 2 == 0 ? 'bottom' : 'top'; //label side will flip flop inperpetuity
    return {
      left: `${basePxArray.x[i] + offset}px`,
      top: `${basePxArray.y[i]}px`,
      labelSide: labelSide,
    };
  });
  return blobPositions;
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const colors = {
  background: {
    complete: { backgroundColor: '#28856F' },
    inProgress: { backgroundColor: '#414753' },
    incomplete: { backgroundColor: '#5B646E' },
  },
  color: {
    complete: { color: '#414753' },
    incomplete: { color: '#CED0D4' },
    selected: {
      background: '#CA7C62',
      cursor: 'pointer',
      transition: '0.3s',
      boxShadow: '0px 0px 5px 5px rgb(202,124,98,.8)',
    },
  },
};

const hoverStyles = isCapacitorNative()
  ? {}
  : {
      cursor: 'pointer',
      transition: '0.3s',
      boxShadow: '0px 0px 5px 5px rgb(70, 159, 106,.5)',
    };

const nameContainerStyles = {
  position: 'absolute',
  zIndex: 11,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const circleContainerStyles = {
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.background.incomplete,
  '&:hover': hoverStyles,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const inProgressContainerStyles = {
  backgroundColor: colors.background.inProgress,
  border: '5px solid #28856F',
  boxSizing: 'border-box',
  '&:hover': hoverStyles,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const completeContainerStyles = {
  backgroundColor: colors.background.complete,
  '&:hover': hoverStyles,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const selectedStyles = {
  width: '62px',
  height: '62px',
  borderRadius: '50%',
  backgroundColor: 'rgba(70, 159, 106, 0.3)',
  border: '1px solid rgba(70, 159, 106, .8)',
  position: 'absolute',
  zIndex: -1,
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const numberStyles = {
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '20px',
};
